"use client";

import React, { useEffect, useState, createContext, useContext } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Container,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  useTheme,
  useMediaQuery,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  FormControl,
  InputLabel,
  Select,
  Paper,
  alpha,
} from "@mui/material";
import {
  Menu as MenuIcon,
  AccountCircle as AccountIcon,
  School as SchoolIcon,
  Description as DescriptionIcon,
  Notifications as NotificationsIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Tune as TuneIcon,
  // Close as CloseIcon,
} from "@mui/icons-material";
import Badge from "@mui/material/Badge";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useStudentUpdateRequest } from "../../hooks/useStudentUpdateRequest";
import { useAuth } from "../../contexts/AuthContext";
import AppSideBar from "./AppSideBar";
import { useSessionClass } from "../../contexts/SessionClassContext";
import { CLASS_OPTIONS_ARRAY } from "components/enum/ClassOptions";
import { SESSION_OPTIONS_ARRAY } from "components/enum/SessionOptions";
import { useTranslation } from "react-i18next";
import { useSchools } from "../../hooks/useSchools";
import { useTeacher } from "hooks/useTeacher";
import { useStudents, ApplicationStudentDTO, useStudentPhoto } from "hooks/useStudents";
import { useSchoolIdForAdmin } from "hooks/useSchoolIdForAdmin";
import { useClasses } from "../../hooks/useClasses";
import { useSessions } from "../../hooks/useSession";
import { useCurrentStudent } from "../../hooks/useCurrentStudent";
import { format } from "date-fns";

export const DRAWER_WIDTH = 240;

const STUDENTS_API = `${process.env.REACT_APP_API_URL}/students`;

const formatReqDate = (val: any): string => {
  if (!val) return '-';
  if (Array.isArray(val) && val.length >= 3) {
    return new Date(val[0], val[1] - 1, val[2]).toLocaleDateString();
  }
  const d = new Date(val);
  return isNaN(d.getTime()) ? '-' : d.toLocaleDateString();
};

const AVATAR_COLORS = ['#3092e3', '#7b1fa2', '#28a745', '#e65100', '#1565c0', '#00838f'];

const STATUS_CONFIG: Record<string, { label: string; bg: string; color: string }> = {
  PENDING:  { label: 'PENDING',  bg: '#fff3e0', color: '#e65100' },
  APPROVED: { label: 'APPROVED', bg: '#e8f5e9', color: '#2e7d32' },
  REJECTED: { label: 'REJECTED', bg: '#fdecea', color: '#c62828' },
};

const PendingRequestItem: React.FC<{
  req: any;
  index: number;
  showActions: boolean;
  processStudentUpdateRequestMutation: any;
}> = ({ req, index, showActions, processStudentUpdateRequestMutation }) => {
  const [expanded, setExpanded] = useState(false);
  const changes: any[] = req.changes || [];

  const { data: studentData } = useQuery({
    queryKey: ['studentById', req.studentId],
    queryFn: async () => {
      const response = await axios.get(`${STUDENTS_API}/${req.studentId}`);
      return response.data;
    },
    enabled: !!req.studentId,
    staleTime: 5 * 60 * 1000,
  });

  const studentName = studentData?.studentName || (req.studentId ? `Student #${req.studentId}` : 'Unknown Student');
  const avatarColor = AVATAR_COLORS[index % AVATAR_COLORS.length];
  const initial = studentName.charAt(0).toUpperCase();
  const fieldSummary = changes.map((c: any) => c.fieldName).join(', ');
  const statusKey = (req.status || 'PENDING') as string;
  const statusCfg = STATUS_CONFIG[statusKey] || STATUS_CONFIG.PENDING;

  return (
    <Box sx={{ borderBottom: '1px solid #f0f0f0' }}>
      {/* Row */}
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: 'flex', gap: 1.5, px: 2, py: 1.75,
          cursor: 'pointer',
          transition: 'background 0.15s',
          '&:hover': { bgcolor: 'rgba(48,146,227,0.05)' },
        }}
      >
        {/* Avatar */}
        <Box sx={{
          width: 42, height: 42, borderRadius: '50%',
          bgcolor: avatarColor, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontWeight: 700, fontSize: '1rem',
        }}>
          {initial}
        </Box>

        {/* Text block */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: '#1a1a2e', lineHeight: 1.3 }}>
            {studentName}
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', color: '#555', mt: 0.25, lineHeight: 1.4,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {changes.length} field{changes.length !== 1 ? 's' : ''} changed{fieldSummary ? `: ${fieldSummary}` : ''}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Typography sx={{ fontSize: '0.73rem', color: '#999' }}>
              {formatReqDate(req.requestedAt)}
            </Typography>
            <Box component="span" sx={{
              px: 0.75, py: 0.1, borderRadius: 0.75,
              fontSize: '0.68rem', fontWeight: 700,
              bgcolor: statusCfg.bg, color: statusCfg.color,
            }}>
              {statusCfg.label}
            </Box>
          </Box>
        </Box>

        {/* Chevron */}
        <Box sx={{
          display: 'flex', alignItems: 'center', color: '#bbb',
          transition: 'transform 0.2s',
          transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
        }}>
          <ExpandMoreIcon fontSize="small" />
        </Box>
      </Box>

      {/* Expanded details */}
      {expanded && (
        <Box sx={{ px: 2, pb: 2, pt: 0.5, bgcolor: '#f8faff', borderTop: '1px solid #e8f0fb' }}>
          {changes.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Typography sx={{ fontSize: '0.75rem', color: '#888', fontWeight: 600, mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Changed Fields
              </Typography>
              {changes.map((change: any) => (
                <Box key={change.fieldName} sx={{ display: 'flex', gap: 1.5, mb: 0.5, fontSize: '0.82rem', alignItems: 'center' }}>
                  <span style={{ fontWeight: 600, minWidth: 120, color: '#444' }}>{change.fieldName}</span>
                  <span style={{ color: '#c62828', background: '#fdecea', padding: '1px 6px', borderRadius: 4 }}>{change.oldValue || '-'}</span>
                  <span style={{ color: '#999' }}>→</span>
                  <span style={{ color: '#2e7d32', background: '#e8f5e9', padding: '1px 6px', borderRadius: 4 }}>{change.newValue || '-'}</span>
                </Box>
              ))}
            </Box>
          )}
          {showActions && (
            <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
              <Button
                size="small" variant="contained" color="success"
                disabled={processStudentUpdateRequestMutation.isLoading}
                onClick={(e) => { e.stopPropagation(); processStudentUpdateRequestMutation.mutate({ studentId: req.studentId!, status: 'APPROVE' }); }}
                sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, px: 2 }}
              >
                Approve
              </Button>
              <Button
                size="small" variant="contained" color="error"
                disabled={processStudentUpdateRequestMutation.isLoading}
                onClick={(e) => { e.stopPropagation(); processStudentUpdateRequestMutation.mutate({ studentId: req.studentId!, status: 'REJECT' }); }}
                sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, px: 2 }}
              >
                Reject
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

// Medium Context
interface MediumContextType {
  selectedMedium: string;
  setSelectedMedium: (medium: string) => void;
  schoolMediums: string[];
}

const MediumContext = createContext<MediumContextType | undefined>(undefined);

export const useMedium = () => {
  const context = useContext(MediumContext);
  if (!context) {
    throw new Error('useMedium must be used within a MediumProvider');
  }
  return context;
};

interface LayoutProps {
  isAdmin?: boolean;
}

const Layout: React.FC<LayoutProps> = () => {
  const { i18n } = useTranslation();
  const {
    user,
    isLoading: authLoading,
    isTeacher,
    isStudent,
    isSchoolAdmin,
    logout,
    isAdmin,
  } = useAuth();
  // Check if user has APP_USER role
  const isAppUser = user?.roles?.includes('APP_USER');
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState<boolean>(() => {
    // Desktop: default open. Mobile: default closed.
    // Persist separately so refresh doesn't re-open on mobile.
    try {
      const key = isMobile ? "sidebarOpen_mobile" : "sidebarOpen_desktop";
      const stored = localStorage.getItem(key);
      if (stored === "true") return true;
      if (stored === "false") return false;
    } catch (error) {
    }
    return !isMobile;
  });
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [studentName, setStudentName] = useState<string>('');
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notificationTab, setNotificationTab] = useState<'all' | 'approved' | 'rejected'>('all');
  // Initialize selectedMedium from localStorage
  // For school admin and teacher, always default to "ALL" (ignore localStorage on init)
  // For students, default to empty string (will be set from student data)
  const [selectedMedium, setSelectedMedium] = useState<string>(() => {
    // For school admin and teacher, always start with "ALL" regardless of localStorage
    // This ensures it defaults to "ALL" on refresh and login
    if (isSchoolAdmin() || isTeacher()) {
      return "ALL";
    }
    // For students, try to load from localStorage or default to empty string
    try {
      const stored = localStorage.getItem('selectedMedium');
      if (stored && typeof stored === 'string' && stored.trim() !== '') {
        return stored;
      }
      return "";
    } catch (error) {
      return "";
    }
  });
  // NOTE: Medium dropdown open-state is left uncontrolled to avoid duplicate menus
  // when the mobile drawer and desktop header selects are both mounted.

  const {
    selectedSession,
    setSelectedSession,
    selectedClass,
    setSelectedClass,
    clearAllStorage,
  } = useSessionClass();
  const { useSchoolById } = useSchools(authLoading || !user, { enableQuery: false });
  // Disable API call for ADMIN/APP_USER/SCHOOL_ADMIN/TEACHER/STUDENT users - they already have schoolId from user context
  const { data: adminSchoolId, isLoading: schoolIdLoading } = useSchoolIdForAdmin(
    isAppUser || isAdmin() || isSchoolAdmin() || isTeacher() || isStudent() ? undefined : user?.id
  );

  // For both teachers and school admins, use their schoolId directly from user context
  // This ensures we use the actual schoolId from the logged-in user
  const schoolId = user?.schoolId;

  const { classesQuery } = useClasses(schoolId ?? undefined);
  const { sessionsQuery, activeSessionQuery } = useSessions(schoolId ?? undefined);

  // For TEACHER: fetch staff details by their login email and filter class list accordingly
  const { useStaffByEmail } = useTeacher(undefined, authLoading);
  const staffByEmailQuery = useStaffByEmail(isTeacher() ? user?.email : undefined);

  const { data: school, isLoading: schoolLoading } = useSchoolById(schoolId ?? undefined);
  const { t } = useTranslation();
  // Only call useStudents for non-students (students use useCurrentStudent)
  const { getStudentByStudentCode, updateStudentWithDTOMutation } = useStudents(false, schoolId ?? undefined, !isStudent());
  // Don't fetch all schools for students, school admin, and teachers - we already have schoolId from login
  const { schoolsQuery } = useSchools(authLoading || !user || isStudent() || isSchoolAdmin() || isTeacher(), { enableQuery: !isStudent() && !isSchoolAdmin() && !isTeacher() });

  // For students, use the current student hook to get student data from login
  const { studentData: currentStudentData, studentLoading: currentStudentLoading } = useCurrentStudent();

  // Fetch the student's photo if the logged-in user is a student
  const { imageUrl: studentPhotoUrl, hasPhoto: hasStudentPhoto } = useStudentPhoto(
    isStudent() ? currentStudentData?.id : undefined
  );

  // Pending update requests for school admin notification
  const { useStudentPendingRequests, processStudentUpdateRequestMutation, useStudentRequestsByStatus } = useStudentUpdateRequest();
  const adminEnabled = isSchoolAdmin() && !authLoading;
  const { data: pendingRequestsData } = useStudentPendingRequests(adminEnabled);
  const { data: approvedRequestsData } = useStudentRequestsByStatus('APPROVED', adminEnabled);
  const { data: rejectedRequestsData } = useStudentRequestsByStatus('REJECTED', adminEnabled);

  const groupByStudent = (requests: any[]): any[] => {
    const map = new Map<number, any>();
    for (const req of requests) {
      const key = req.studentId;
      if (!key) continue;
      if (map.has(key)) {
        const existing = map.get(key)!;
        existing.changes = [...(existing.changes || []), ...(req.changes || [])];
      } else {
        map.set(key, { ...req, changes: [...(req.changes || [])] });
      }
    }
    return Array.from(map.values());
  };

  const pendingRequests = groupByStudent(pendingRequestsData || []);
  const approvedRequests = groupByStudent(approvedRequestsData || []);
  const rejectedRequests = groupByStudent(rejectedRequestsData || []);

  // Helper function to get class sort value (numeric part)
  const getClassSortValue = (className?: string) => {
    if (!className) return Number.MAX_SAFE_INTEGER;

    const normalized = className.trim().toLowerCase().replace(/[^a-z0-9]/g, "");

    // Ensure Nursery -> LKG -> UKG always appear before Class 1.
    // (These classes usually don't contain digits, so they'd otherwise go to the end.)
    if (normalized.includes("nursery")) return -3;
    if (normalized.includes("lkg")) return -2;
    if (normalized.includes("ukg")) return -1;

    const numericMatch = className.match(/\d+/);
    if (numericMatch) return parseInt(numericMatch[0], 10);

    return Number.MAX_SAFE_INTEGER;
  };

  // Helper function to get section sort value (A=1, B=2, C=3, etc.)
  const getSectionSortValue = (section?: string | null) => {
    if (!section) return 0;
    const normalized = section.trim().toUpperCase();
    if (normalized.length === 0) return 0;
    const charCode = normalized.charCodeAt(0);
    if (charCode >= 65 && charCode <= 90) {
      return charCode - 64; // A=1, B=2, C=3, etc.
    }
    return Number.MAX_SAFE_INTEGER;
  };

  // Sort classes: first by numeric class number, then by section letter
  const sortedClassOptions = React.useMemo(() => {
    const classes = classesQuery.data ?? [];
    return [...classes].sort((a: any, b: any) => {
      // First sort by class number (8, 9, 10, etc.)
      const classDiff = getClassSortValue(a.name) - getClassSortValue(b.name);
      if (classDiff !== 0) return classDiff;

      // Then sort by section letter (A, B, C, D, etc.)
      const sectionDiff = getSectionSortValue(a.section) - getSectionSortValue(b.section);
      if (sectionDiff !== 0) return sectionDiff;

      // Final fallback to alphabetical
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [classesQuery.data]);

  const normalizeTeacherClassToken = (value: string) =>
    (value || "")
      .toString()
      .trim()
      // Make teacher strings like "6th A" match class records like { name: "6th", section: "A" }
      // by stripping all non-alphanumeric characters (spaces, dashes, etc.)
      .replace(/[^a-z0-9]/gi, "")
      .toLowerCase();

  const teacherAssignedClasses = React.useMemo(() => {
    if (!isTeacher()) return null;
    const teacherOfClass =
      (staffByEmailQuery.data as any)?.teacherOfClass ??
      (staffByEmailQuery.data as any)?.teacherOfClasses ??
      (staffByEmailQuery.data as any)?.TeacherOfClasses;
    if (!Array.isArray(teacherOfClass) || teacherOfClass.length === 0) return [];

    return teacherOfClass
      .filter(Boolean)
      .map((x: any) => normalizeTeacherClassToken(String(x)))
      .filter((x: string) => x.length > 0);
  }, [isTeacher, staffByEmailQuery.data]);

  // Filter options for students to show only their enrolled class/session/medium
  // Use currentStudentData from login for students
  const studentData = isStudent() ? currentStudentData : null;

  /** Sentinel section for UI-only "3rd-ALL" style entries (not from API). */
  const GRADE_ALL_SECTION = "__GRADE_ALL__";
  /** Sentinel section for UI-only "Class-ALL" entry (not from API). */
  const CLASS_ALL_SECTION = "__CLASS_ALL__";

  const classOptionsWithGradeAll = React.useMemo(() => {
    const base = sortedClassOptions;
    if (!base.length) return base;

    const gradeNames = Array.from(new Set(base.map((c: any) => c.name).filter(Boolean))) as string[];
    gradeNames.sort((a, b) => getClassSortValue(a) - getClassSortValue(b));

    const schoolIdFallback = base[0]?.schoolId ?? 0;
    const merged: any[] = [];
    let synthIdx = 0;

    // "Class-ALL" entry: used to fetch across all classes for selected session
    merged.push({
      id: -1_500_000 - synthIdx++,
      name: "Class",
      section: CLASS_ALL_SECTION,
      schoolId: schoolIdFallback,
    });

    for (const grade of gradeNames) {
      merged.push({
        id: -1_000_000 - synthIdx++,
        name: grade,
        section: GRADE_ALL_SECTION,
        schoolId: schoolIdFallback,
      });
      merged.push(...base.filter((c: any) => c.name === grade));
    }
    return merged;
  }, [sortedClassOptions]);

  const teacherFilteredClassOptions = React.useMemo(() => {
    if (!isTeacher()) return null;

    // While loading teacher mapping, don't over-restrict; UI will settle once fetched.
    if (staffByEmailQuery.isLoading || staffByEmailQuery.isFetching) {
      return classOptionsWithGradeAll;
    }

    const tokens = teacherAssignedClasses;
    if (!tokens) return classOptionsWithGradeAll;
    if (tokens.length === 0) return [];

    const isGradeAllToken = (token: string) => token.endsWith("all");

    const exactSet = new Set<string>();
    const gradeAllSet = new Set<string>();

    for (const token of tokens) {
      if (isGradeAllToken(token)) {
        const gradeName = token.replace(/-?all$/i, "").trim();
        if (gradeName) gradeAllSet.add(gradeName);
      } else {
        exactSet.add(token);
      }
    }

    const classKey = (name?: string, section?: string | null) =>
      normalizeTeacherClassToken(`${name || ""}${section || ""}`);

    // Filter out the synthetic "-ALL" entries unless teacher has grade-all assignment for that grade.
    return classOptionsWithGradeAll.filter((cls: any) => {
      if (!cls?.name) return false;

      // Always keep Class-ALL entry available.
      if (cls.section === CLASS_ALL_SECTION) return true;

      // Synthetic grade ALL entries
      if (cls.section === GRADE_ALL_SECTION) {
        const gradeToken = normalizeTeacherClassToken(cls.name);
        return gradeAllSet.has(gradeToken);
      }

      const key = classKey(cls.name, cls.section ?? "");
      if (exactSet.has(key)) return true;

      const gradeToken = normalizeTeacherClassToken(cls.name);
      return gradeAllSet.has(gradeToken);
    });
  }, [
    isTeacher,
    staffByEmailQuery.isLoading,
    staffByEmailQuery.isFetching,
    classOptionsWithGradeAll,
    teacherAssignedClasses,
  ]);

  const classOptions =
    isStudent() && studentData?.schoolClassId
      ? sortedClassOptions.filter((cls: any) => cls.id === studentData.schoolClassId)
      : isTeacher()
        ? (teacherFilteredClassOptions ?? classOptionsWithGradeAll)
        : classOptionsWithGradeAll;

  const sessionData = isStudent() && studentData?.sessionId
    ? (sessionsQuery.data ?? []).filter((ses: any) => ses.id === studentData.sessionId)
    : (sessionsQuery.data ?? []);

  const formatClassOption = (cls?: { name?: string; section?: string | null }) => {
    if (!cls?.name) return "";
    if (cls.section === CLASS_ALL_SECTION) return `Class-ALL`;
    if (cls.section === GRADE_ALL_SECTION) return `${cls.name}-ALL`;
    return cls.section ? `${cls.name} - ${cls.section}` : cls.name;
  };

  const selectedClassValue = selectedClass ? formatClassOption(selectedClass) : "";
  const selectedSessionValue = selectedSession?.name ?? "";

  const currentYear = new Date().getFullYear();
  // Show all sessions (both active and inactive)
  const sessionOptions = sessionData.map((session) => session.name);

  useEffect(() => {
    // Check if selectedSession still exists in the sessions list (not deleted)
    const resolveNextSession = () =>
      activeSessionQuery.data ??
      sessionData.find((s) => s.isActive) ??
      sessionData[sessionData.length - 1];

    const sessionStillExists =
      !!selectedSession &&
      sessionData.some((s) => {
        if (selectedSession.id && s.id) {
          return s.id === selectedSession.id;
        }
        return s.name === selectedSession.name;
      });

    if (selectedSession) {
      const matchingSession = sessionData.find((s) =>
        selectedSession.id && s.id
          ? s.id === selectedSession.id
          : s.name === selectedSession.name
      );
      if (matchingSession) {
        const hasChanged =
          matchingSession.isActive !== selectedSession.isActive ||
          matchingSession.name !== selectedSession.name ||
          matchingSession.updatedAt !== (selectedSession as any)?.updatedAt;
        if (hasChanged) {
          setSelectedSession(matchingSession);
          return;
        }
      }
    }

    if (sessionData.length === 0) {
      // Don't clear selectedSession if we're still loading - it might be restored from localStorage
      if (selectedSession && !sessionsQuery.isLoading) {
        setSelectedSession(null);
      }
      return;
    }

    if (selectedSession && !sessionStillExists) {
      const nextSession = resolveNextSession();
      if (nextSession) {
        setSelectedSession(nextSession);
      } else {
        setSelectedSession(null);
      }
      return;
    }

    // Only set default session if no session is selected AND we're not loading (to avoid overriding localStorage restore)
    if (!selectedSession && !sessionsQuery.isLoading) {
      const nextSession = resolveNextSession();
      if (nextSession) {
        setSelectedSession(nextSession);
      }
    }
  }, [
    sessionData,
    selectedSession,
    setSelectedSession,
    activeSessionQuery.data,
    sessionsQuery.isLoading,
  ]);

  useEffect(() => {

    if (classOptions.length === 0) {
      // Don't clear selectedClass if we're still loading - it might be restored from localStorage
      if (selectedClass && !classesQuery.isLoading) {
        setSelectedClass(null);
      }
      return;
    }

    // Check if stored class exists in current class options
    const classStillExists =
      !!selectedClass &&
      classOptions.some((cls) => {
        // Prefer id match, but also allow matching by (name + section) for synthetic "*-ALL" entries
        // whose IDs may differ across components.
        const idMatch =
          selectedClass.id != null && cls.id != null
            ? cls.id === selectedClass.id
            : false;
        const nameSectionMatch =
          cls.name === selectedClass.name &&
          (cls.section ?? "") === (selectedClass.section ?? "");
        return idMatch || nameSectionMatch;
      });

    // If stored class exists, ensure it's properly set (in case of ID mismatch but same name)
    if (selectedClass && classStillExists) {
      const matchingClass = classOptions.find((cls) => {
        const idMatch =
          selectedClass.id != null && cls.id != null
            ? cls.id === selectedClass.id
            : false;
        const nameSectionMatch =
          cls.name === selectedClass.name &&
          (cls.section ?? "") === (selectedClass.section ?? "");
        return idMatch || nameSectionMatch;
      });

      // Update to the matching class from options to ensure we have the latest data
      if (matchingClass && (matchingClass.id !== selectedClass.id || matchingClass.name !== selectedClass.name)) {
        setSelectedClass(matchingClass);
      }
      return; // Don't override if class exists
    }

    // Only set fallback if stored class doesn't exist anymore
    if (selectedClass && !classStillExists) {
      const fallbackClass =
        classOptions[classOptions.length - 1] ?? classOptions[0];
      if (fallbackClass) {
        setSelectedClass(fallbackClass);
      } else {
        setSelectedClass(null);
      }
      return;
    }

    // Only set default if no class is selected AND we're not loading (to avoid overriding localStorage restore)
    if (!selectedClass && !classesQuery.isLoading) {
      const defaultClass =
        classOptions[classOptions.length - 1] ?? classOptions[0];
      if (defaultClass) {
        setSelectedClass(defaultClass);
      }
    }
  }, [classOptions, selectedClass, setSelectedClass, classesQuery.isLoading]);

  // Save selectedMedium to localStorage whenever it changes (only for school admin and teacher)
  // Only save if it's not "ALL" or if user explicitly selected a medium
  useEffect(() => {
    // For school admin and teacher, save to localStorage (but always default to "ALL" on init)
    // For students, save their medium to localStorage
    if (selectedMedium && (isSchoolAdmin() || isTeacher() || isStudent())) {
      try {
        localStorage.setItem('selectedMedium', selectedMedium);
      } catch (error) {
      }
    }
  }, [selectedMedium, isSchoolAdmin, isTeacher, isStudent]);

  // Ensure medium is set to "ALL" for school admin and teacher on mount/refresh/login
  useEffect(() => {
    // Only set default medium for school admin and teacher (not for students)
    // Students have their own logic that sets medium from student data
    if (!authLoading && user && (isSchoolAdmin() || isTeacher())) {
      // Always set to "ALL" on mount/refresh/login for school admin and teacher
      // This ensures it defaults to "ALL" even if localStorage has a different value
      // Only update if it's not already "ALL" to avoid unnecessary re-renders
      if (selectedMedium !== "ALL") {
        setSelectedMedium("ALL");
        // Clear any non-"ALL" value from localStorage for school admin/teacher
        try {
          localStorage.setItem('selectedMedium', "ALL");
        } catch (error) {
        }
      }
    }
  }, [authLoading, user?.id]); // Run when user loads or user ID changes (login/logout), not on every selectedMedium change

  // Clear localStorage when school changes
  useEffect(() => {
    // Clear previous selections when school changes
    if (schoolId) {
      // Only clear if we're switching to a different school
      const lastSchoolId = localStorage.getItem('lastSchoolId');
      if (lastSchoolId && lastSchoolId !== schoolId.toString()) {
        // Clear all storage and set new school ID
        clearAllStorage();
        localStorage.setItem('lastSchoolId', schoolId.toString());
      } else if (!lastSchoolId) {
        // First time setting school
        localStorage.setItem('lastSchoolId', schoolId.toString());
      }
    }
  }, [schoolId, clearAllStorage]);

  // Debug selectedMedium changes
  useEffect(() => {
  }, [selectedMedium]);

  // Set class, session, and medium from student data for students
  // Always update to match student's actual class and session (handles promotion updates)
  useEffect(() => {
    if (isStudent() && !authLoading && classesQuery.data && sessionsQuery.data && currentStudentData) {
      // Always set class from student's actual schoolClassId (from their record)
      if (currentStudentData.schoolClassId) {
        const studentClass = classesQuery.data?.find(
          (cls: any) => cls.id === currentStudentData.schoolClassId
        );
        if (studentClass) {
          // Always update to match student's actual class (handles promotion updates)
          const currentClassId = selectedClass?.id;
          if (currentClassId !== studentClass.id) {
            setSelectedClass(studentClass);
          }
        }
      }

      // Always set session from student's actual sessionId (from their record)
      if (currentStudentData.sessionId) {
        const studentSession = sessionsQuery.data?.find(
          (ses: any) => ses.id === currentStudentData.sessionId
        );
        if (studentSession) {
          // Always update to match student's actual session (handles promotion updates)
          const currentSessionId = selectedSession?.id;
          if (currentSessionId !== studentSession.id) {
            setSelectedSession(studentSession);
          }
        }
      }

      // Set medium from student data
      if (currentStudentData.medium) {
        // Always update to match student's actual medium
        if (selectedMedium !== currentStudentData.medium) {
          setSelectedMedium(currentStudentData.medium);
        }
      }
    }
  }, [
    isStudent,
    authLoading,
    currentStudentData,
    classesQuery.data,
    sessionsQuery.data,
    selectedClass,
    selectedSession,
    selectedMedium,
    setSelectedClass,
    setSelectedSession,
  ]);

  // When switching between mobile/desktop breakpoints, restore the persisted state for that mode.
  useEffect(() => {
    try {
      const key = isMobile ? "sidebarOpen_mobile" : "sidebarOpen_desktop";
      const stored = localStorage.getItem(key);
      if (stored === "true") {
        setMobileOpen(true);
      } else if (stored === "false") {
        setMobileOpen(false);
      } else {
        setMobileOpen(!isMobile);
      }
    } catch (error) {
      setMobileOpen(!isMobile);
    }
  }, [isMobile]);

  const handleDrawerToggle = () => {
    setMobileOpen((prev) => {
      const next = !prev;
      try {
        const key = isMobile ? "sidebarOpen_mobile" : "sidebarOpen_desktop";
        localStorage.setItem(key, String(next));
      } catch (error) {
      }
      return next;
    });
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  // Fetch student data on mount if user is a student
  useEffect(() => {
    const fetchStudentName = async () => {
      if (isStudent() && user) {
        try {
          // Use currentStudentData from login
          if (currentStudentData) {
            // Use current student data directly for display name
            const completeStudentData = currentStudentData;
            if (completeStudentData) {
              const name = completeStudentData.studentName ||
                user?.username ||
                '';
              if (name) {
                setStudentName(name);
                return;
              }
            }
          }
          // Fallback to username if studentData is not loaded yet
          if (user?.username && !studentName) {
            setStudentName(user.username);
          }
        } catch (error) {
          // Fallback to username if available
          if (user?.username && !studentName) {
            setStudentName(user.username);
          }
        }
      }
    };

    if (!authLoading && user) {
      fetchStudentName();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStudent, user, authLoading, currentStudentData, currentStudentLoading]);

  // Handle Profile click for STUDENT - navigate to profile page
  const handleProfileClick = () => {
    handleProfileMenuClose();
    if (isStudent()) {
      navigate('/student/profile');
    }
  };


  const handleLogout = () => {
    handleProfileMenuClose();
    // Clear all localStorage using the context function
    clearAllStorage();
    logout();
    // Navigate to landing page after logout
    navigate('/');
  };

  // Get school mediums as array - filter for students to show only their medium
  const schoolMediums = isStudent() && studentData?.medium
    ? [studentData.medium]
    : (school?.medium
      ? (Array.isArray(school.medium) ? school.medium : [school.medium])
      : []);

  return (
    <MediumContext.Provider value={{
      selectedMedium,
      setSelectedMedium,
      schoolMediums
    }}>
      <Box sx={{ minHeight: "100vh" }}>
        <AppBar
          position="fixed"
          sx={{
            width: "100%",
            left: 0,
            background: "linear-gradient(135deg, #ffffff 0%, #f8fafe 100%)",
            color: "text.primary",
            borderBottom: "none",
            boxShadow: "0 2px 20px rgba(0,0,0,0.08)",
            backdropFilter: "blur(10px)",
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
        >
          <Toolbar
            sx={{
              pl: { xs: 1, sm: 2, md: 2 },
              flexWrap: { xs: "wrap", md: "nowrap" },
              alignItems: "center",
              rowGap: { xs: 0.75, md: 0 },
              py: { xs: 1, md: 0 },
            }}
          >
            {!isAdmin() && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexGrow: 1,
                  minWidth: 0,
                }}
              >
                <IconButton
                  color="inherit"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{
                    mr: 2,
                    fontSize: "1.5rem",
                    padding: "8px",
                    color: "#333",
                    "&:hover": {
                      transform: "scale(1.05)",
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                      transition: "all 0.2s ease-in-out"
                    }
                  }}
                >
                  <MenuIcon />
                </IconButton>
                {/* Welcome message for students - right after hamburger icon */}
                {isStudent() && (studentName || user?.username) && (
                  <Typography
                    variant="h6"
                    sx={{
                      color: 'black',
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
                      whiteSpace: { xs: 'normal', md: 'nowrap' },
                      overflow: { xs: 'visible', md: 'hidden' },
                      textOverflow: { xs: 'clip', md: 'ellipsis' },
                      wordBreak: { xs: 'break-word', md: 'normal' },
                      flexGrow: 1,
                      minWidth: 0,
                      mr: { xs: 1, md: 2 },
                      lineHeight: 1.25,
                    }}
                  >
                    {t('welcome') || 'Welcome'}, {studentName || user?.username || ''}
                  </Typography>
                )}
              </Box>
            )}

            {isAdmin() && (
              <Box sx={{ padding: "12px 0", flex: 1 }}>
                <Typography
                  variant="h3"
                  fontWeight="bold"
                  sx={{
                    background: "#3092e3",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    fontSize: { xs: "1.5rem", md: "2rem" },
                    lineHeight: 1.2,
                  }}
                >
                  {t("admin_dashboard")}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: "text.secondary",
                    fontSize: { xs: "0.875rem", md: "1rem" },
                    fontWeight: 500,
                    opacity: 0.8
                  }}
                >
                  {t("multi_tenant_school_management_system")}
                </Typography>
              </Box>
            )}

            <Box
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
              flex={1}
              width="100%"
              sx={{ ml: 'auto' }}
            >
              {!isAdmin() && (
                <>
                  {/* Right side - Dropdowns */}
                  <Box
                    display="flex"
                    alignItems="center"
                    sx={{
                      flexShrink: 0,
                      ml: 'auto',
                      display: { xs: "none", md: "flex" },
                    }}
                  >
                    <FormControl
                      size="small"
                      sx={{
                        minWidth: 140,
                        mx: 1,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                          background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(240,248,255,0.9) 100%)',
                          backdropFilter: 'blur(10px)',
                          transition: 'all 0.3s ease-in-out',
                          boxShadow: '0 2px 8px rgba(50, 140, 249, 0.1)',
                          '& fieldset': {
                            border: '1px solid rgba(50, 140, 249, 0.2)',
                          },
                          '&:hover fieldset': {
                            border: '1px solid rgba(50, 140, 249, 0.3)',
                          },
                          '&.Mui-focused fieldset': {
                            border: '1px solid #3092e3',
                          },
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 16px rgba(50, 140, 249, 0.15)',
                          },
                          '&.Mui-focused': {
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 16px rgba(50, 140, 249, 0.2)',
                          }
                        },
                        '& .MuiInputLabel-root': {
                          color: '#3092e3',
                          fontWeight: 600,
                          '&.Mui-focused': {
                            color: '#3092e3',
                          }
                        },
                        '& .MuiSelect-select': {
                          fontWeight: 500,
                          color: '#333',
                        },
                        '& .MuiSelect-select.Mui-selected': {
                          backgroundColor: 'rgba(50, 140, 249, 0.15)',
                          color: '#3092e3',
                          fontWeight: 600,
                        }
                      }}
                    >
                      <InputLabel id="class-select-label">{t("class")}</InputLabel>
                      <Select
                        labelId="class-select-label"
                        value={selectedClassValue}
                        onChange={(e) => {
                          const value = e.target.value as string;
                          const classData = classOptions.find(
                            (c) => formatClassOption(c) === value
                          );
                          setSelectedClass(classData || null);
                        }}
                        label={t("class")}
                        disabled={classesQuery.isLoading || !schoolId || isStudent()}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              borderRadius: 1,
                              boxShadow: '0 8px 32px rgba(50, 140, 249, 0.15)',
                              border: '1px solid rgba(50, 140, 249, 0.1)',
                              maxHeight: 300,
                              '& .MuiMenuItem-root': {
                                borderRadius: 1,
                                mx: 1,
                                my: 0.5,
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                  backgroundColor: 'rgba(50, 140, 249, 0.08)',
                                  transform: 'translateX(4px)',
                                },
                                '&.Mui-selected': {
                                  backgroundColor: 'rgba(50, 140, 249, 0.15)',
                                  color: '#3092e3',
                                  fontWeight: 600,
                                }
                              }
                            }
                          }
                        }}
                      >
                        {classesQuery.isLoading ? (
                          <MenuItem disabled>{t("loading")}</MenuItem>
                        ) : !classOptions.length ? (
                          <MenuItem disabled>{t("no_classes_available")}</MenuItem>
                        ) : (
                          classOptions.map((cls: any) => (
                            <MenuItem
                              key={`${cls.id ?? "x"}-${cls.name}-${cls.section ?? ""}`}
                              value={formatClassOption(cls)}
                            >
                              {formatClassOption(cls)}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    </FormControl>
                    <FormControl
                      size="small"
                      sx={{
                        minWidth: 160,
                        mx: 1,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                          background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(240,248,255,0.9) 100%)',
                          backdropFilter: 'blur(10px)',
                          transition: 'all 0.3s ease-in-out',
                          boxShadow: '0 2px 8px rgba(50, 140, 249, 0.1)',
                          '& fieldset': {
                            border: '1px solid rgba(50, 140, 249, 0.2)',
                          },
                          '&:hover fieldset': {
                            border: '1px solid rgba(50, 140, 249, 0.3)',
                          },
                          '&.Mui-focused fieldset': {
                            border: '1px solid #3092e3',
                          },
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 16px rgba(50, 140, 249, 0.15)',
                          },
                          '&.Mui-focused': {
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 16px rgba(50, 140, 249, 0.2)',
                          }
                        },
                        '& .MuiInputLabel-root': {
                          color: '#3092e3',
                          fontWeight: 600,
                          '&.Mui-focused': {
                            color: '#3092e3',
                          }
                        },
                        '& .MuiSelect-select': {
                          fontWeight: 500,
                          color: '#333',
                        }
                      }}
                    >
                      <InputLabel id="session-select-label">{t("session")}</InputLabel>
                      <Select
                        labelId="session-select-label"
                        value={selectedSessionValue}
                        onChange={(e) => {
                          const value = e.target.value as string;
                          const session = sessionData.find(
                            (s) => s.name === value
                          );
                          setSelectedSession(session || null);
                        }}
                        label={t("session")}
                        disabled={sessionsQuery.isLoading || !schoolId || isStudent()}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              borderRadius: 1,
                              boxShadow: '0 8px 32px rgba(50, 140, 249, 0.15)',
                              border: '1px solid rgba(50, 140, 249, 0.1)',
                              maxHeight: 300,
                              '& .MuiMenuItem-root': {
                                borderRadius: 1,
                                mx: 1,
                                my: 0.5,
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                  backgroundColor: 'rgba(50, 140, 249, 0.08)',
                                  transform: 'translateX(4px)',
                                },
                                '&.Mui-selected': {
                                  backgroundColor: 'rgba(50, 140, 249, 0.15)',
                                  color: '#3092e3',
                                  fontWeight: 600,
                                }
                              }
                            }
                          }
                        }}
                      >
                        {sessionsQuery.isLoading ? (
                          <MenuItem disabled>{t("loading")}</MenuItem>
                        ) : sessionData.length === 0 ? (
                          <MenuItem disabled>{t("no_sessions_available")}</MenuItem>
                        ) : (
                          sessionData.map((session) => (
                            <MenuItem key={session.id ?? session.name} value={session.name}>
                              {session.name}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    </FormControl>
                    {!isAppUser && (
                      <FormControl
                        size="small"
                        sx={{
                          minWidth: 140,
                          mx: 1,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1,
                            background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(240,248,255,0.9) 100%)',
                            backdropFilter: 'blur(10px)',
                            transition: 'all 0.3s ease-in-out',
                            boxShadow: '0 2px 8px rgba(50, 140, 249, 0.1)',
                            '& fieldset': {
                              border: '1px solid rgba(50, 140, 249, 0.2)',
                            },
                            '&:hover fieldset': {
                              border: '1px solid rgba(50, 140, 249, 0.3)',
                            },
                            '&.Mui-focused fieldset': {
                              border: '1px solid #3092e3',
                            },
                            '&:hover': {
                              transform: 'translateY(-1px)',
                              boxShadow: '0 4px 16px rgba(50, 140, 249, 0.15)',
                            },
                            '&.Mui-focused': {
                              transform: 'translateY(-1px)',
                              boxShadow: '0 4px 16px rgba(50, 140, 249, 0.2)',
                            }
                          },
                          '& .MuiInputLabel-root': {
                            color: '#3092e3',
                            fontWeight: 600,
                            '&.Mui-focused': {
                              color: '#3092e3',
                            }
                          },
                          '& .MuiSelect-select': {
                            fontWeight: 500,
                            color: '#333',
                          },
                          '& .MuiSelect-select.Mui-selected': {
                            backgroundColor: 'rgba(50, 140, 249, 0.15)',
                            color: '#3092e3',
                            fontWeight: 600,
                          }
                        }}
                      >
                        <InputLabel id="medium-select-label">{t("medium")}</InputLabel>
                        <Select
                          labelId="medium-select-label"
                          value={selectedMedium || ""}
                          displayEmpty
                          onChange={(e) => {
                            const selectedValue = e.target.value as string;
                            setSelectedMedium(selectedValue);
                          }}
                          renderValue={(value) => {
                            if (!value || value === "") {
                              return <em></em>;
                            }
                            return value;
                          }}
                          label={t("medium")}
                          disabled={schoolLoading || !schoolId || !school?.medium || isStudent()}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                borderRadius: 1,
                                boxShadow: '0 8px 32px rgba(50, 140, 249, 0.15)',
                                border: '1px solid rgba(50, 140, 249, 0.1)',
                                maxHeight: 300,
                                '& .MuiMenuItem-root': {
                                  borderRadius: 1,
                                  mx: 1,
                                  my: 0.5,
                                  transition: 'all 0.2s ease-in-out',
                                  '&:hover': {
                                    backgroundColor: 'rgba(50, 140, 249, 0.08)',
                                    transform: 'translateX(4px)',
                                  },
                                  '&.Mui-selected': {
                                    backgroundColor: 'rgba(50, 140, 249, 0.15)',
                                    color: '#3092e3',
                                    fontWeight: 600,
                                  }
                                }
                              }
                            }
                          }}
                        >
                          {schoolLoading ? (
                            <MenuItem disabled>{t("loading")}</MenuItem>
                          ) : !school?.medium || (Array.isArray(school.medium) && school.medium.length === 0) ? (
                            <MenuItem disabled>{t("no_medium_available")}</MenuItem>
                          ) : (
                            <>
                              {Array.isArray(school?.medium) ? school.medium.map((medium) => (
                                <MenuItem
                                  key={medium}
                                  value={medium}
                                  onClick={() => {
                                    setSelectedMedium(medium);
                                  }}
                                >
                                  {medium}
                                </MenuItem>
                              )) : school?.medium ? (
                                <MenuItem
                                  value={typeof school?.medium === 'string' ? school.medium : (school?.medium && school.medium[0]) || ''}
                                  onClick={() => {
                                    const mediumValue = typeof school?.medium === 'string' ? school.medium : (school?.medium && school.medium[0]) || '';
                                    setSelectedMedium(mediumValue);
                                  }}
                                >
                                  {typeof school?.medium === 'string' ? school.medium : (school?.medium && school.medium[0]) || ''}
                                </MenuItem>
                              ) : null}
                              <MenuItem
                                value="ALL"
                                onClick={() => {
                                  setSelectedMedium("ALL");
                                }}
                              >
                                <strong>ALL</strong>
                              </MenuItem>
                            </>
                          )}
                        </Select>
                      </FormControl>
                    )}
                  </Box>
                </>
              )}

              {/* Right side - Language selector and User avatar */}
              <Box
                display="flex"
                alignItems="center"
                sx={{
                  ml: isAdmin() ? 'auto' : 0,
                  flexShrink: 0
                }}
              >
                <FormControl
                  size="small"
                  sx={{
                    minWidth: { xs: 140, md: 150 },
                    mx: { xs: 0.5, md: 1 },
                    display: { xs: isStudent() ? "none" : "flex", md: "flex" },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(240,248,255,0.9) 100%)',
                      backdropFilter: 'blur(10px)',
                      transition: 'all 0.3s ease-in-out',
                      boxShadow: '0 2px 8px rgba(50, 140, 249, 0.1)',
                      '& fieldset': {
                        border: '1px solid rgba(50, 140, 249, 0.2)',
                      },
                      '&:hover fieldset': {
                        border: '1px solid rgba(50, 140, 249, 0.3)',
                      },
                      '&.Mui-focused fieldset': {
                        border: '1px solid #3092e3',
                      },
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 16px rgba(50, 140, 249, 0.15)',
                      },
                      '&.Mui-focused': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 16px rgba(50, 140, 249, 0.2)',
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: '#3092e3',
                      fontWeight: 600,
                      '&.Mui-focused': {
                        color: '#3092e3',
                      }
                    },
                    '& .MuiSelect-select': {
                      fontWeight: 500,
                      color: '#333',
                    }
                  }}
                >
                  <InputLabel id="language-select-label"> {t("language")}</InputLabel>
                  <Select
                    labelId="language-select-label"
                    label={t("language")}
                    value={i18n.language}
                    onChange={(e) => {
                      const newLang = e.target.value;
                      i18n.changeLanguage(newLang);
                      // Persist language selection
                      try {
                        localStorage.setItem('selectedLanguage', newLang);
                        localStorage.setItem('i18nextLng', newLang);
                      } catch (error) {
                      }
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          borderRadius: 1,
                          boxShadow: '0 8px 32px rgba(50, 140, 249, 0.15)',
                          border: '1px solid rgba(50, 140, 249, 0.1)',
                          '& .MuiMenuItem-root': {
                            borderRadius: 1,
                            mx: 1,
                            my: 0.5,
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              backgroundColor: 'rgba(50, 140, 249, 0.08)',
                              transform: 'translateX(4px)',
                            },
                            '&.Mui-selected': {
                              backgroundColor: 'rgba(50, 140, 249, 0.15)',
                              color: '#3092e3',
                              fontWeight: 600,
                            }
                          }
                        }
                      }
                    }}
                  >
                    <MenuItem value="hi">हिन्दी</MenuItem>
                    <MenuItem value="en">English</MenuItem>
                  </Select>
                </FormControl>

                {/* Notification bell for school admin */}
                {isSchoolAdmin() && (
                  <IconButton
                    onClick={() => setNotificationOpen(true)}
                    size="small"
                    sx={{ ml: 1, color: '#3092e3' }}
                  >
                    <Badge badgeContent={pendingRequests.length > 0 ? pendingRequests.length : undefined} color="error">
                      <NotificationsIcon />
                    </Badge>
                  </IconButton>
                )}

                {!isAdmin() && (
                  <IconButton
                    onClick={() => setMobileFiltersOpen(true)}
                    size="small"
                    sx={{
                      display: { xs: "inline-flex", md: "none" },
                      ml: { xs: 0.5, md: 0 },
                      mr: 0.5,
                      color: "#333",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": { transform: "scale(1.05)" },
                    }}
                    aria-label="Open filters"
                  >
                    <TuneIcon fontSize="small" />
                  </IconButton>
                )}

                {user && (
                  <>
                    <IconButton
                      onClick={handleProfileMenuOpen}
                      size="small"
                      sx={{
                        ml: { xs: 0.5, md: 2 },
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'scale(1.05)',
                        }
                      }}
                    >
                      <Avatar
                        src={(isStudent() && hasStudentPhoto && studentPhotoUrl) ? studentPhotoUrl : undefined}
                        sx={{
                          width: 36,
                          height: 36,
                          background: "#3092e3",
                          boxShadow: '0 4px 12px rgba(1, 104, 234, 0.3)',
                          fontSize: '0.875rem',
                          fontWeight: 600
                        }}
                      >
                        {isStudent() 
                          ? (studentName ? studentName.charAt(0).toUpperCase() : user.username.charAt(0).toUpperCase())
                          : user.username.charAt(0).toUpperCase()
                        }
                      </Avatar>
                    </IconButton>
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleProfileMenuClose}
                      onClick={handleProfileMenuClose}
                      PaperProps={{
                        sx: {
                          borderRadius: 3,
                          boxShadow: '0 8px 32px rgba(59, 162, 245, 0.15)',
                          border: '1px solid rgba(59, 162, 245, 0.1)',
                          minWidth: 180,
                          '& .MuiMenuItem-root': {
                            borderRadius: 1,
                            mx: 1,
                            my: 0.5,
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              backgroundColor: 'rgba(59, 162, 245, 0.08)',
                              transform: 'translateX(4px)',
                            }
                          }
                        }
                      }}
                    >
                      <MenuItem onClick={isStudent() ? handleProfileClick : undefined}>
                        <ListItemIcon>
                          <AccountIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>{t("profile_management")}</ListItemText>
                      </MenuItem>
                      {isStudent() && user?.students && user.students.length > 1 && (
                        <MenuItem onClick={() => navigate("/student/select-profile")}>
                          <ListItemIcon>
                            <SchoolIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>{t("switch_profile") || "Switch Profile"}</ListItemText>
                        </MenuItem>
                      )}
                      <Divider />
                      <MenuItem
                        onClick={handleLogout}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'rgba(244, 67, 54, 0.08)',
                            '& .MuiListItemText-primary': {
                              color: '#f44336',
                            }
                          }
                        }}
                      >
                        <ListItemText
                          sx={{
                            '& .MuiListItemText-primary': {
                              color: '#f44336',
                              fontWeight: 500,
                            }
                          }}
                        >
                          {t("logout")}
                        </ListItemText>
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </Box>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Mobile filters drawer (only for non-admin) */}
        <Drawer
          anchor="right"
          open={mobileFiltersOpen && !isAdmin()}
          onClose={() => setMobileFiltersOpen(false)}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            sx: {
              width: { xs: "min(84vw, 300px)", sm: 300 },
              mt: { xs: "65px", md: 0 },
              height: { xs: "calc(100% - 65px)", md: "100%" },
              maxHeight: { xs: "calc(100% - 65px)", md: "100%" },
              p: 2,
              background: "linear-gradient(135deg, #ffffff 0%, #f8fafe 100%)",
            },
          }}
          sx={{
            display: { xs: "block", md: "none" },
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#333" }}>
              {t("filters")}
            </Typography>
            <IconButton
              onClick={() => setMobileFiltersOpen(false)}
              size="small"
              sx={{ color: "#333" }}
              aria-label="Close filters"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Same dropdowns, stacked for mobile */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {isStudent() && (
              <FormControl
                size="small"
                fullWidth
                sx={{
                  minWidth: 0,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1,
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(240,248,255,0.9) 100%)',
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.3s ease-in-out',
                    boxShadow: '0 2px 8px rgba(50, 140, 249, 0.1)',
                    '& fieldset': {
                      border: '1px solid rgba(50, 140, 249, 0.2)',
                    },
                    '&:hover fieldset': {
                      border: '1px solid rgba(50, 140, 249, 0.3)',
                    },
                    '&.Mui-focused fieldset': {
                      border: '1px solid #3092e3',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#3092e3',
                    fontWeight: 600,
                    '&.Mui-focused': {
                      color: '#3092e3',
                    }
                  },
                  '& .MuiSelect-select': {
                    fontWeight: 500,
                    color: '#333',
                  }
                }}
              >
                <InputLabel id="language-select-label-mobile">{t("language")}</InputLabel>
                <Select
                  labelId="language-select-label-mobile"
                  label={t("language")}
                  value={i18n.language}
                  onChange={(e) => {
                    const newLang = e.target.value;
                    i18n.changeLanguage(newLang);
                    try {
                      localStorage.setItem('selectedLanguage', newLang);
                      localStorage.setItem('i18nextLng', newLang);
                    } catch (error) {
                    }
                    setMobileFiltersOpen(false);
                  }}
                >
                  <MenuItem value="hi">हिन्दी</MenuItem>
                  <MenuItem value="en">English</MenuItem>
                </Select>
              </FormControl>
            )}

            <FormControl
              size="small"
              fullWidth
              sx={{
                minWidth: 0,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(240,248,255,0.9) 100%)',
                  backdropFilter: 'blur(10px)',
                  transition: 'all 0.3s ease-in-out',
                  boxShadow: '0 2px 8px rgba(50, 140, 249, 0.1)',
                  '& fieldset': {
                    border: '1px solid rgba(50, 140, 249, 0.2)',
                  },
                  '&:hover fieldset': {
                    border: '1px solid rgba(50, 140, 249, 0.3)',
                  },
                  '&.Mui-focused fieldset': {
                    border: '1px solid #3092e3',
                  },
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 16px rgba(50, 140, 249, 0.15)',
                  },
                  '&.Mui-focused': {
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 16px rgba(50, 140, 249, 0.2)',
                  }
                },
                '& .MuiInputLabel-root': {
                  color: '#3092e3',
                  fontWeight: 600,
                  '&.Mui-focused': {
                    color: '#3092e3',
                  }
                },
                '& .MuiSelect-select': {
                  fontWeight: 500,
                  color: '#333',
                },
                '& .MuiSelect-select.Mui-selected': {
                  backgroundColor: 'rgba(50, 140, 249, 0.15)',
                  color: '#3092e3',
                  fontWeight: 600,
                }
              }}
            >
              <InputLabel id="class-select-label-mobile">{t("class")}</InputLabel>
              <Select
                labelId="class-select-label-mobile"
                value={selectedClassValue}
                onChange={(e) => {
                  const value = e.target.value as string;
                  const classData = classOptions.find(
                    (c) => formatClassOption(c) === value
                  );
                  setSelectedClass(classData || null);
                  setMobileFiltersOpen(false);
                }}
                label={t("class")}
                disabled={classesQuery.isLoading || !schoolId || isStudent()}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: 1,
                      boxShadow: '0 8px 32px rgba(50, 140, 249, 0.15)',
                      border: '1px solid rgba(50, 140, 249, 0.1)',
                      maxHeight: 300,
                      '& .MuiMenuItem-root': {
                        borderRadius: 1,
                        mx: 1,
                        my: 0.5,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          backgroundColor: 'rgba(50, 140, 249, 0.08)',
                          transform: 'translateX(4px)',
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(50, 140, 249, 0.15)',
                          color: '#3092e3',
                          fontWeight: 600,
                        }
                      }
                    }
                  }
                }}
              >
                {classesQuery.isLoading ? (
                  <MenuItem disabled>{t("loading")}</MenuItem>
                ) : !classOptions.length ? (
                  <MenuItem disabled>{t("no_classes_available")}</MenuItem>
                ) : (
                  classOptions.map((cls: any) => (
                    <MenuItem
                      key={cls.id}
                      value={formatClassOption(cls)}
                      onClick={() => setMobileFiltersOpen(false)}
                    >
                      {formatClassOption(cls)}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            <FormControl
              size="small"
              fullWidth
              sx={{
                minWidth: 0,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(240,248,255,0.9) 100%)',
                  backdropFilter: 'blur(10px)',
                  transition: 'all 0.3s ease-in-out',
                  boxShadow: '0 2px 8px rgba(50, 140, 249, 0.1)',
                  '& fieldset': {
                    border: '1px solid rgba(50, 140, 249, 0.2)',
                  },
                  '&:hover fieldset': {
                    border: '1px solid rgba(50, 140, 249, 0.3)',
                  },
                  '&.Mui-focused fieldset': {
                    border: '1px solid #3092e3',
                  },
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 16px rgba(50, 140, 249, 0.15)',
                  },
                  '&.Mui-focused': {
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 16px rgba(50, 140, 249, 0.2)',
                  }
                },
                '& .MuiInputLabel-root': {
                  color: '#3092e3',
                  fontWeight: 600,
                  '&.Mui-focused': {
                    color: '#3092e3',
                  }
                },
                '& .MuiSelect-select': {
                  fontWeight: 500,
                  color: '#333',
                }
              }}
            >
              <InputLabel id="session-select-label-mobile">{t("session")}</InputLabel>
              <Select
                labelId="session-select-label-mobile"
                value={selectedSessionValue}
                onChange={(e) => {
                  const value = e.target.value as string;
                  const session = sessionData.find(
                    (s) => s.name === value
                  );
                  setSelectedSession(session || null);
                  setMobileFiltersOpen(false);
                }}
                label={t("session")}
                disabled={sessionsQuery.isLoading || !schoolId || isStudent()}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: 1,
                      boxShadow: '0 8px 32px rgba(50, 140, 249, 0.15)',
                      border: '1px solid rgba(50, 140, 249, 0.1)',
                      maxHeight: 300,
                      '& .MuiMenuItem-root': {
                        borderRadius: 1,
                        mx: 1,
                        my: 0.5,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          backgroundColor: 'rgba(50, 140, 249, 0.08)',
                          transform: 'translateX(4px)',
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(50, 140, 249, 0.15)',
                          color: '#3092e3',
                          fontWeight: 600,
                        }
                      }
                    }
                  }
                }}
              >
                {sessionsQuery.isLoading ? (
                  <MenuItem disabled>{t("loading")}</MenuItem>
                ) : sessionData.length === 0 ? (
                  <MenuItem disabled>{t("no_sessions_available")}</MenuItem>
                ) : (
                  sessionData.map((session) => (
                    <MenuItem
                      key={session.id ?? session.name}
                      value={session.name}
                      onClick={() => setMobileFiltersOpen(false)}
                    >
                      {session.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            {!isAppUser && (
              <FormControl
                size="small"
                fullWidth
                sx={{
                  minWidth: 0,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1,
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(240,248,255,0.9) 100%)',
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.3s ease-in-out',
                    boxShadow: '0 2px 8px rgba(50, 140, 249, 0.1)',
                    '& fieldset': {
                      border: '1px solid rgba(50, 140, 249, 0.2)',
                    },
                    '&:hover fieldset': {
                      border: '1px solid rgba(50, 140, 249, 0.3)',
                    },
                    '&.Mui-focused fieldset': {
                      border: '1px solid #3092e3',
                    },
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 16px rgba(50, 140, 249, 0.15)',
                    },
                    '&.Mui-focused': {
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 16px rgba(50, 140, 249, 0.2)',
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: '#3092e3',
                    fontWeight: 600,
                    '&.Mui-focused': {
                      color: '#3092e3',
                    }
                  },
                  '& .MuiSelect-select': {
                    fontWeight: 500,
                    color: '#333',
                  },
                  '& .MuiSelect-select.Mui-selected': {
                    backgroundColor: 'rgba(50, 140, 249, 0.15)',
                    color: '#3092e3',
                    fontWeight: 600,
                  },
                }}
              >
                <InputLabel id="medium-select-label-mobile">{t("medium")}</InputLabel>
                <Select
                  labelId="medium-select-label-mobile"
                  value={selectedMedium || ""}
                  displayEmpty
                  onChange={(e) => {
                    const selectedValue = e.target.value as string;
                    setSelectedMedium(selectedValue);
                    setMobileFiltersOpen(false);
                  }}
                  renderValue={(value) => {
                    if (!value || value === "") {
                      return <em></em>;
                    }
                    return value;
                  }}
                  label={t("medium")}
                  disabled={schoolLoading || !schoolId || !school?.medium || isStudent()}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: 1,
                        boxShadow: '0 8px 32px rgba(50, 140, 249, 0.15)',
                        border: '1px solid rgba(50, 140, 249, 0.1)',
                        maxHeight: 300,
                        '& .MuiMenuItem-root': {
                          borderRadius: 1,
                          mx: 1,
                          my: 0.5,
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            backgroundColor: 'rgba(50, 140, 249, 0.08)',
                            transform: 'translateX(4px)',
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(50, 140, 249, 0.15)',
                            color: '#3092e3',
                            fontWeight: 600,
                          }
                        }
                      }
                    }
                  }}
                >
                  {schoolLoading ? (
                    <MenuItem disabled>{t("loading")}</MenuItem>
                  ) : !school?.medium || (Array.isArray(school.medium) && school.medium.length === 0) ? (
                    <MenuItem disabled>{t("no_medium_available")}</MenuItem>
                  ) : (
                    <>
                      {Array.isArray(school?.medium) ? school.medium.map((medium) => (
                        <MenuItem
                          key={medium}
                          value={medium}
                          onClick={() => {
                            setSelectedMedium(medium);
                            setMobileFiltersOpen(false);
                          }}
                        >
                          {medium}
                        </MenuItem>
                      )) : school?.medium ? (
                        <MenuItem
                          value={typeof school?.medium === 'string' ? school.medium : (school?.medium && school.medium[0]) || ''}
                          onClick={() => {
                            const mediumValue = typeof school?.medium === 'string' ? school.medium : (school?.medium && school.medium[0]) || '';
                            setSelectedMedium(mediumValue);
                            setMobileFiltersOpen(false);
                          }}
                        >
                          {typeof school?.medium === 'string' ? school.medium : (school?.medium && school.medium[0]) || ''}
                        </MenuItem>
                      ) : null}
                      <MenuItem
                        value="ALL"
                        onClick={() => {
                          setSelectedMedium("ALL");
                          setMobileFiltersOpen(false);
                        }}
                      >
                        <strong>ALL</strong>
                      </MenuItem>
                    </>
                  )}
                </Select>
              </FormControl>
            )}
          </Box>
        </Drawer>

        {!user?.roles.includes("ADMIN") && (
          <AppSideBar
            mobileOpen={mobileOpen}
            handleDrawerToggle={handleDrawerToggle}
            selectedClassId={selectedClass?.id}
            selectedSessionId={selectedSession?.id}
          />
        )}

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 0, md: 3 },
            mt: "64px",
            ml: isAdmin()
              ? "0px"
              : {
                xs: "0px",
                md: mobileOpen ? `${DRAWER_WIDTH}px` : "0px"
              },
            width: isAdmin()
              ? "100%"
              : {
                xs: "100%",
                md: mobileOpen ? `calc(100% - ${DRAWER_WIDTH}px)` : "100%"
              },
            bgcolor: "#fafbfc",
            minHeight: "100vh",
            transition: "margin-left 0.3s ease-in-out, width 0.3s ease-in-out",
          }}
        >
          {user?.roles.includes("ADMIN") ? (
            <Box width="100%">
              <Outlet />
            </Box>
          ) : (
            <Container maxWidth="xl" sx={{ p: { xs: 0, md: 3 } }}>
              <Outlet />
            </Container>
          )}
        </Box>

      </Box>

      {/* Notification drawer for school admin - pending student update requests */}
      <Drawer
        anchor="right"
        open={notificationOpen}
        onClose={() => setNotificationOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 400 }, display: 'flex', flexDirection: 'column', borderRadius: '12px 0 0 12px' } }}
      >
        {/* Dark header */}
        <Box sx={{
          bgcolor: '#1a1a2e', color: '#fff',
          px: 2.5, py: 2, flexShrink: 0,
          display: 'flex', alignItems: 'center',
        }}>
          <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1rem', flex: 1 }}>
            Notifications
          </Typography>
          {pendingRequests.length > 0 && (
            <Badge badgeContent={pendingRequests.length} color="error" sx={{ mr: 2 }} />
          )}
          <IconButton onClick={() => setNotificationOpen(false)} size="small" sx={{ color: '#fff', p: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Tab bar */}
        <Box sx={{ display: 'flex', borderBottom: '1px solid #e8e8e8', bgcolor: '#fff', flexShrink: 0 }}>
          {(['all', 'approved', 'rejected'] as const).map((tab) => {
            const label = tab === 'all' ? 'All' : tab === 'approved' ? 'Approved' : 'Rejected';
            const count = tab === 'all' ? pendingRequests.length : tab === 'approved' ? approvedRequests.length : rejectedRequests.length;
            const isActive = notificationTab === tab;
            return (
              <Box
                key={tab}
                onClick={() => setNotificationTab(tab)}
                sx={{
                  px: 2, py: 1.25, fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer',
                  color: isActive ? '#3092e3' : '#888',
                  borderBottom: isActive ? '2px solid #3092e3' : '2px solid transparent',
                  display: 'flex', alignItems: 'center', gap: 0.75,
                  '&:hover': { color: '#3092e3' },
                }}
              >
                {label}
                {count > 0 && (
                  <Box component="span" sx={{
                    px: 0.75, py: 0.1, borderRadius: '10px',
                    fontSize: '0.7rem', fontWeight: 700,
                    bgcolor: isActive ? '#3092e3' : '#e0e0e0',
                    color: isActive ? '#fff' : '#666',
                  }}>
                    {count}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        {/* List */}
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {(() => {
            const displayRequests = notificationTab === 'all' ? pendingRequests : notificationTab === 'approved' ? approvedRequests : rejectedRequests;
            const showActions = notificationTab === 'all';
            if (displayRequests.length === 0) {
              return (
                <Box sx={{ p: 4, textAlign: 'center', color: '#aaa' }}>
                  <NotificationsIcon sx={{ fontSize: 40, mb: 1, color: '#ddd' }} />
                  <Typography variant="body2">
                    {notificationTab === 'all' ? 'No pending student update requests.' : `No ${notificationTab} requests.`}
                  </Typography>
                </Box>
              );
            }
            return displayRequests.map((req: any, idx: number) => (
              <PendingRequestItem
                key={req.id ?? idx}
                req={req}
                index={idx}
                showActions={showActions}
                processStudentUpdateRequestMutation={processStudentUpdateRequestMutation}
              />
            ));
          })()}
        </Box>
      </Drawer>
    </MediumContext.Provider>
  );
};

export default Layout;